import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EditOutlined } from "@mui/icons-material";
import { Package } from "lucide-react";
import CustomTextField from "../../common/CustomTextField";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { ProductItemInfo, initialValuesOfProductItem } from "../../../redux/slice/product/createProductSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

interface ProductItemProps {
  open: boolean;
  onClose: () => void;
  addProductItem: (productItem: ProductItemInfo, ) => void;
  productItemDetails: ProductItemInfo;
  isEdit: boolean;
  productHcpcsCodeAvailable?:boolean
  isKitChecked?:boolean
  manufacturerCartOption?: string;
}

const ProductItemPopup: React.FC<ProductItemProps> = ({
  open,
  onClose,
  addProductItem,
  productItemDetails,
  isEdit,
  productHcpcsCodeAvailable,
  isKitChecked,
  manufacturerCartOption
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { control, handleSubmit, setValue, getValues, reset, watch, formState: { errors } } = useForm<ProductItemInfo>({defaultValues:initialValuesOfProductItem});
  const [frequency, setfrequency] = useState<CodeTable[]>([]);
  //CS-1187 - Start
  const [collagenKitDay, setCollagenKitDay] = useState<CodeTable[]>([]);
  //CS-1187 - End
   const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const [usageTypes, setUsageTypes] = useState<CodeTable[]>([]);

  useEffect(()=>{
    if (open){
      reset(productItemDetails)
    }
    if (!open){
      setDefaultValues();
    }
  },[open]);

  const setDefaultValues = useCallback(()=>{
    reset();
  },[]);


  useEffect(() => {
      if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
        const sortedProductCategoryList = [
          ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "frequency"),
        ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
        setfrequency(sortedProductCategoryList);

        const sortedUsageTypes = [
          ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductItemUsageType"),
        ];
        setUsageTypes(sortedUsageTypes);

        //CS-1187 - Start
        const sortedCollegenKitDayList = [
          ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "WoundTypeDay"),
        ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
        setCollagenKitDay(sortedCollegenKitDayList);
        //CS-1187 - End
      }
    }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const productFrequencyoption = useMemo(() => {
      return frequency.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }));
    }, [frequency]);

  const productItemUsageTypeOptions = useMemo(() => {
    if (getValues('usageType')==null) {
      const primaryItem = usageTypes.find(item => item.keyName === 'Primary');
      setValue("usageType", primaryItem?.displayValue || '');
    }
    return usageTypes.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [usageTypes]);

    //CS-1187 - Start
    const collagenKitDayOption = useMemo(() => {
    
      const uniqueMap = new Map<string, typeof collagenKitDay[0]>();
    
      collagenKitDay.forEach(item => {
        if (!uniqueMap.has(item.displayValue)) {
          uniqueMap.set(item.displayValue, item);
        }
      });
    
      return Array.from(uniqueMap.values()).map(item => ({
        label: item.displayValue,
        value: item.displayValue
      }));
    
    }, [collagenKitDay]);
    
    
    //CS-1187 - End
  const onSubmit = useCallback((data: ProductItemInfo) => {
    
    
    let obj: ProductItemInfo = {
      ...data,
      allowableUnitPrice: "",
      listPrice: "",
      customPrice: "",
      inactiveDate: data.inactiveDate ? dayjs(data.inactiveDate).format("MM/DD/YYYY") : "",
    }
    addProductItem(obj);
    onClose();
  }, [open]);

  const isUsageTypeRequired = manufacturerCartOption === "Option 2";

  const title = isEdit ? "Update Product Item" : "Add Product Item";
  const subtitle =
    "Enter SKU, sizing, frequency, and usage for this line item.";
  const submitLabel = isEdit ? "Update Product Item" : "Add Product Item";
  const ariaDescId = "product-item-dialog-description";
  const formErrors = errors as Record<string, { message?: string } | undefined>;

  return (
    <>
      <Dialog
        open={open}
        keepMounted
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{ sx: paperSx(isMobile) }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        aria-describedby={ariaDescId}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(tokens.accent)}>
              <Package size={20} color={tokens.accent} strokeWidth={1.8} />
            </Box>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography id={ariaDescId} sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                {subtitle}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              ...bodySx,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >
            <Grid container spacing={2}>
                {(!productHcpcsCodeAvailable || isEdit) &&(<>
                <Grid item md={4} xs={12}>
                  <Box>
                  <FieldLabel label="HCPCS Code" tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.hcpcsCode)}>
                  <CustomTextField
                    label=""
                    controllerName="hcpcsCode"
                    control={control}
                    size="small"
                    // rules={!isKitChecked ?{ required: "HCPCS Code is required." }:""}
                  />
                  </Box>
                  </Box>
                </Grid>
                </>)}
                <Grid item md={4} xs={12}>
                  <Box>
                  <FieldLabel label="SKU #" required tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.sku)}>
                  <CustomTextField
                    label=""
                    controllerName="sku"
                    control={control}
                    size="small"
                    rules={{ required: "SKU # is required." }}
                  />
                  </Box>
                  </Box>
                </Grid>
                {/* CS-1187 - Start */}
                {isKitChecked && (<>
                <Grid item md={4} xs={12}>
                  <Box>
                  <FieldLabel label="Daily Supply" required={isKitChecked} tokens={tokens} />
                  <Box sx={dropdownWrapSx(!!formErrors.noOfDays)}>
                  <CustomDropdownField
                    label=""
                    name="noOfDays"
                    control={control}
                    size="small"
                    options={collagenKitDayOption} 
                    rules={isKitChecked?{ required: "Daily Supply is required." }:{}}
                  />
                  </Box>
                  </Box>
                </Grid>
                </>)}
                {/* CS-1187- End */}
                <Grid item md={4} xs={12}>
                  <Box>
                  <FieldLabel label="Size" required={!isKitChecked} tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.size)}>
                  <CustomTextField
                    label=""
                    controllerName="size"
                    control={control}
                    size="small"
                    rules={!isKitChecked?{ required: "Size is required." }:""}
                  />
                  </Box>
                  </Box>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Box>
                  <FieldLabel label="# of Units" required={!isKitChecked} tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.units)}>
                  <CustomTextField
                    label=""
                    controllerName="units"
                    control={control}
                    size="small"
                    rules={!isKitChecked?{ required: "# of Units is required." }:""}
                    isNumberOnly={true} 
                  />
                  </Box>
                  </Box>
                </Grid>
                

                <Grid item md={6} xs={12}>
                <Box>
                <FieldLabel label="Usage Type" required={isUsageTypeRequired} tokens={tokens} />
                <Box sx={dropdownWrapSx(!!errors.usageType)}>
                <CustomDropdownField 
                  name="usageType" 
                  control={control} 
                  label="" 
                  size="small"
                  options={productItemUsageTypeOptions} 
                  rules={isUsageTypeRequired ? { required: "Usage Type is required." } : {}} 
                />
                </Box>
                </Box>
              </Grid>

                <Grid item md={6} xs={12}>
                <Box>
                <FieldLabel label="Product Frequency" tokens={tokens} />
                <Box sx={dropdownWrapSx(!!formErrors.frequency)}>
                <CustomDropdownField name="frequency" control={control} label="" size="small" options={productFrequencyoption} />
                </Box>
                </Box>
              </Grid>

              <Grid item md={12} xs={12}>
                  <Box>
                  <FieldLabel label="Description" tokens={tokens} />
                  <Box sx={inputFieldSx(!!formErrors.productItemDescription)}>
                  <CustomTextField
                    label=""
                    controllerName="productItemDescription"
                    control={control}
                    size="small"
                    rules={{maxLength: { value: 50, message: "Maximum length of description is 50" }}}
                  />
                  </Box>
                  </Box>
                </Grid>

                {/* {watch("productItemId") ? (
                  <Grid item md={4} xs={12}>
                    <CustomDatePicker
                      label="Inactive Date"
                      openTo="day"
                      controllerName="inactiveDate"
                      control={control}
                      format="MM/DD/YYYY"
                    />
                  </Grid>
                ) : (
                  <></>
                )} */}
            </Grid>
          </Box>

          <Box sx={footerSx}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                component="button"
                type="submit"
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <EditOutlined sx={{ fontSize: "1.1rem" }} />
                {submitLabel}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                sx={{
                  ...cancelBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "1px solid",
                  cursor: "pointer",
                }}
              >
                Cancel
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ProductItemPopup;
